import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '../../../auth/useAuth0';
import { useBusinessContext } from '../../../context/BusinessContext';
import { formatPhone, isPhoneComplete, resolvePhoneCountry } from '../../../util/phone';
import { useTimeFormat, formatClock, formatHHmm } from '../../../util/timeFormat';
import TimeSelect from '../../commons/datetime/TimeSelect';
import { useAlert } from '../../../context/alert/AlertContext';
import { useConfirm } from '../../../context/confirm/ConfirmContext';
import { usePlan } from '../../../context/PlanContext';
import { useNavigation } from '../../../context/NavigationContext';
import LoadingComponent from '../../commons/LoadingComponent';
import { TeamMember } from '../../../model/teammember/TeamMember';
import { RoleBusiness } from '../../../model/role/RoleBusiness';
import { Service } from '../../../model/service/Service';
import { TeamMemberInput } from '../../../service/teammember/TeamMemberInput';
import { TeamMemberWorkingHoursInput } from '../../../service/teammember/TeamMemberWorkingHoursInput';
import {
    deleteTeamMember,
    saveTeamMember,
    getTeamMemberCommission,
    saveTeamMemberCommission,
    setTeamMemberActive,
} from '../../../service/teammember/team-member-service';
import { getCommissionsByTeamMember } from '../../../service/payment/transaction-service';
import { CommissionRecord } from '../../../model/teammember/Commission';
import { getMyPermissionGrant, PermissionGrant } from '../../../service/permission/permission-service';
import { BusinessHours } from '../../../model/business/BusinessHours';
import { useQueryClient } from '@tanstack/react-query';
import { useTeamMembers, useBusinessRoles, useServices, useBusinessHours } from '../../../hooks/queries';
import {
    PASTEL_PALETTE,
    colorForIndex,
    resolveTeamMemberColor,
    readableTextColor,
} from '../../../util/teamMemberColor';
import Drawer from '../../commons/drawer/Drawer';
import { NumberWithSuffix } from '../../commons/settings-row/SettingsRow';
import Pagination from '../../commons/pagination/Pagination';
import ConfirmModal from '../../commons/confirm-modal/ConfirmModal';
import { Appointment } from '../../../model/appointment/Appointment';
import { APPOINTMENT_PAYMENT_LABEL } from '../../../model/appointment/AppointmentPaymentStatus';
import { Transaction } from '../../../model/payment/Transaction';
import { getAppointmentsByTeamMember } from '../../../service/appointment/appointment-service';
import { getTransactionsByAppointment } from '../../../service/payment/transaction-service';
import { MENU } from '../../util/Utils';
import ShareBookingModal from '../services/components/ShareBookingModal';
import './TeamMemberComponent.css';

/** Drawer tabs for the team-member editor. */
type TmTab = 'details' | 'appointments' | 'payments' | 'commission';

const DAYS_OF_WEEK = [
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY',
    'SUNDAY',
] as const;

/** "HH:mm[:ss]" → minutes since midnight (0–1439), or null. */
const hhmmToMin = (t?: string | null): number | null => {
    const m = (t || '').slice(0, 5).match(/^(\d{1,2}):(\d{2})$/);
    return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
};
/** End/close minutes — midnight (00:00) counts as end-of-day (1440). */
const endMin = (t?: string | null): number | null => {
    const v = hhmmToMin(t);
    return v === 0 ? 24 * 60 : v;
};

const DAY_LABEL: Record<string, string> = {
    MONDAY: 'Mon',
    TUESDAY: 'Tue',
    WEDNESDAY: 'Wed',
    THURSDAY: 'Thu',
    FRIDAY: 'Fri',
    SATURDAY: 'Sat',
    SUNDAY: 'Sun',
};

type WorkingHourRow = {
    id?: string | null;
    dayOfWeek: string;
    enabled: boolean;
    startTime: string;
    endTime: string;
    allowBookingOnIntervalTime: boolean;
    startIntervalTime: string;
    endIntervalTime: string;
    /** Early-morning block end ("HH:mm") that occurs the next day, or null when off. */
    overnightEnd: string | null;
};

interface Draft {
    id?: string;
    firstName: string;
    lastName: string;
    publicName: string;
    email: string;
    phone: string;
    /** Always a valid hex string — defaults to a palette color when the team member has none. */
    color: string;
    allowOnlineBooking: boolean;
    hasAppAccess: boolean;
    roleBusinessId: string;
    serviceIds: string[];
    workingHours: WorkingHourRow[];
}

/**
 * Builds the working-hour rows for a draft. For days the member hasn't
 * configured, the start/end default to the COMPANY's business hours for that day
 * (falling back to 09:00–18:00 only when the company has no hours). When creating
 * a NEW member (no `existing`), days the company is open are pre-enabled so the
 * member mirrors the business by default; for an existing member, unset days stay
 * disabled (we never silently turn on a day they didn't have).
 */
const defaultRows = (
    existing?: TeamMember | null,
    businessHours?: BusinessHours[],
): WorkingHourRow[] =>
    DAYS_OF_WEEK.map((day) => {
        const existingRow = existing?.workingHours?.find((w) => w.dayOfWeek === day);
        const biz = businessHours?.find(
            (b) => String(b.dayOfWeek).toUpperCase() === day,
        );
        const isNew = !existing;
        return {
            id: existingRow?.id ?? null,
            dayOfWeek: day,
            enabled: existingRow ? true : isNew ? !!biz : false,
            startTime: existingRow?.startTime?.slice(0, 5) ?? biz?.openTime?.slice(0, 5) ?? '09:00',
            endTime: existingRow?.endTime?.slice(0, 5) ?? biz?.closeTime?.slice(0, 5) ?? '18:00',
            allowBookingOnIntervalTime: existingRow?.allowBookingOnIntervalTime ?? false,
            startIntervalTime: existingRow?.startIntervalTime?.slice(0, 5) ?? '12:00',
            endIntervalTime: existingRow?.endIntervalTime?.slice(0, 5) ?? '13:00',
            overnightEnd: existingRow?.overnightEnd?.slice(0, 5) ?? null,
        };
    });

const emptyDraft = (initialColor: string, businessHours?: BusinessHours[]): Draft => ({
    firstName: '',
    lastName: '',
    publicName: '',
    email: '',
    phone: '',
    color: initialColor,
    allowOnlineBooking: true,
    hasAppAccess: false,
    roleBusinessId: '',
    serviceIds: [],
    workingHours: defaultRows(null, businessHours),
});

const TeamMemberComponent: React.FC = () => {
    const { t } = useTranslation();
    /** Localized short weekday label (Seg/Ter/… in PT) with an English fallback. */
    const dayLabel = (day: string) => t(`settings.calendar.days.${day}`, DAY_LABEL[day] ?? day);

    /** A member's service ids keep only the ones that still exist (a deleted
     *  service can linger as a stale id) and de-duped — so the list count matches
     *  the chips in the editor, and saving drops the stale ids. */
    const validServiceIds = (ids?: string[] | null): string[] =>
        Array.from(new Set(ids ?? [])).filter((id) => services.some((s) => s.id === id));
    const confirmDialog = useConfirm();
    const { plan, openUpsell, reload: reloadPlan } = usePlan();
    const timeFmt = useTimeFormat();
    const { getAccessTokenSilently } = useAuth0();
    const { selectedBusiness } = useBusinessContext();
    const { setAlert } = useAlert();
    const { pendingTeamMemberId, setPendingTeamMemberId, setActiveMenu, setPendingAppointmentId } = useNavigation();

    /** Drawer tabs: Details (form), Appointments, Payments. */
    const [activeTab, setActiveTab] = useState<TmTab>('details');
    /** Appointments + transactions for the currently-edited team member. */
    const [tmAppointments, setTmAppointments] = useState<Appointment[]>([]);
    const [tmTxns, setTmTxns] = useState<Transaction[]>([]);
    const [loadingTmData, setLoadingTmData] = useState(false);

    /** TEAM grant — gates the entire screen (view) and management actions (edit). */
    const [teamGrant, setTeamGrant] = useState<PermissionGrant | null>(null);
    const [teamGrantLoaded, setTeamGrantLoaded] = useState(false);
    const canViewTeam = !!teamGrant?.canView;
    const canEditTeam = !!teamGrant?.canEdit;

    /** COMMISSIONS grant — gates the Comissão tab. View shows it (own-scope only
     *  the caller's own member); editing rates requires all-scope (manager). */
    const [commissionGrant, setCommissionGrant] = useState<PermissionGrant | null>(null);
    const canViewCommission = !!commissionGrant?.canView;
    const canEditCommission = !!commissionGrant?.canEdit && !!commissionGrant?.allScope;

    /** Deactivation confirm modal state. */
    const [deactivateTarget, setDeactivateTarget] = useState<TeamMember | null>(null);
    const [deactivateBusy, setDeactivateBusy] = useState(false);
    /** Editable config: default % (string for clearable input) + overrides map serviceId→% string. */
    const [commDefault, setCommDefault] = useState<string>('');
    const [commOverrides, setCommOverrides] = useState<Record<string, string>>({});
    const [commHistory, setCommHistory] = useState<CommissionRecord[]>([]);
    const [commLoading, setCommLoading] = useState(false);
    const [commSaving, setCommSaving] = useState(false);
    const [addServiceId, setAddServiceId] = useState<string>('');

    /** Bounce into Appointments with this id pre-selected — bypasses period. */
    const openAppointmentDetail = (apptId: string | number) => {
        setPendingAppointmentId(String(apptId));
        setActiveMenu(MENU.APPOINTMENTS);
    };

    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [draft, setDraft] = useState<Draft | null>(null);
    const [saving, setSaving] = useState(false);

    /** Share booking link modal. */
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [shareMemberName, setShareMemberName] = useState<string | undefined>(undefined);
    /** The shared member's service ids → the modal opens professional-first,
     *  pre-selecting this member and listing only the services they perform. */
    const [shareMemberServiceIds, setShareMemberServiceIds] = useState<string[] | undefined>(undefined);

    /** Text typed into the "Add service…" search box for filtering matches. */
    const [svcSearch, setSvcSearch] = useState('');
    /** Controls the visibility of the matches dropdown below the search box. */
    const [svcDropdownOpen, setSvcDropdownOpen] = useState(false);

    /** Pagination + free-text filter for the team-members list. */
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [search, setSearch] = useState('');
    /** Active / deactivated tab. Deactivated members live in their own tab so
     *  they can be reviewed and reactivated without cluttering the active list. */
    const [statusTab, setStatusTab] = useState<'active' | 'inactive'>('active');

    const businessIdNum = Number(selectedBusiness?.id) || 0;
    const businessIdStr = selectedBusiness?.id ? String(selectedBusiness.id) : '';

    // Cached via React Query — revisiting the Team screen is instant and
    // refreshes in the background instead of refetching all four lists.
    const queryClient = useQueryClient();
    const teamMembersQuery = useTeamMembers(businessIdStr);
    const rolesQuery = useBusinessRoles(businessIdStr);
    const servicesQuery = useServices(businessIdNum);
    const businessHoursQuery = useBusinessHours(businessIdNum || undefined);
    const teamMembers: TeamMember[] = teamMembersQuery.data ?? [];
    const roles: RoleBusiness[] = rolesQuery.data ?? [];
    // Events are managed on their own tab (their professionals are picked in the
    // event drawer), so they never appear in the member's service picker.
    const services: Service[] = (servicesQuery.data ?? []).filter((sv: Service) => (sv.kind ?? 'SERVICE') !== 'EVENT');
    const businessHours: BusinessHours[] = businessHoursQuery.data ?? [];
    const loading = teamMembersQuery.isLoading;
    /** Optimistic patch of the cached team-members list (activate/deactivate). */
    const patchTeamMembers = (updater: (prev: TeamMember[]) => TeamMember[]) =>
        queryClient.setQueryData<TeamMember[]>(['teamMembers', businessIdStr], (prev: TeamMember[] | undefined) => updater(prev ?? []));
    const reload = () => queryClient.invalidateQueries({ queryKey: ['teamMembers', businessIdStr] });

    useEffect(() => {
        if (teamMembersQuery.error) {
            setAlert('danger', teamMembersQuery.error instanceof Error ? teamMembersQuery.error.message : t('teamMember.alerts.failLoad'));
        }
    }, [teamMembersQuery.error, setAlert, t]);

    // Auto-select a team member when navigation requests it (e.g. "View" button
    // from the Roles & Permission screen).
    useEffect(() => {
        if (!pendingTeamMemberId || teamMembers.length === 0) return;
        const target = teamMembers.find((t) => String(t.id) === String(pendingTeamMemberId));
        if (target) {
            const idx = teamMembers.findIndex((t) => t.id === target.id);
            setSelectedId(target.id);
            setDraft({
                id: target.id,
                firstName: target.firstName ?? '',
                lastName: target.lastName ?? '',
                publicName: target.publicName ?? '',
                email: target.email ?? '',
                phone: target.phone ?? '',
                color: resolveTeamMemberColor(target, idx),
                allowOnlineBooking: target.allowOnlineBooking ?? true,
                hasAppAccess: target.hasAppAccess ?? false,
                roleBusinessId: target.roleBusinessId ?? '',
                serviceIds: validServiceIds(target.serviceIds),
                workingHours: defaultRows(target, businessHours),
            });
        }
        setPendingTeamMemberId(null);
    }, [pendingTeamMemberId, teamMembers, setPendingTeamMemberId, businessHours]);

    /** When the editor opens for an existing team member, fetch their
     *  appointments + every transaction tied to those appointments. We
     *  do it once on draft change rather than on every tab click so
     *  the count badge above each tab is always accurate. */
    useEffect(() => {
        if (!draft?.id) {
            setTmAppointments([]);
            setTmTxns([]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                setLoadingTmData(true);
                const token = await getAccessTokenSilently();
                const appts = await getAppointmentsByTeamMember(String(draft.id), token);
                if (cancelled) return;
                setTmAppointments(appts);
                // Aggregate transactions across all their appointments in
                // parallel. Per-id failures collapse to an empty list so
                // one stale appt doesn't kill the rollup.
                const txLists = await Promise.all(
                    appts.map((a) =>
                        getTransactionsByAppointment(String(a.id), token).catch(() => [] as Transaction[])
                    )
                );
                if (cancelled) return;
                setTmTxns(txLists.flat());
            } catch (e) {
                if (!cancelled) setAlert('danger', e instanceof Error ? e.message : t('teamMember.alerts.failHistory'));
            } finally {
                if (!cancelled) setLoadingTmData(false);
            }
        })();
        return () => { cancelled = true; };
    }, [draft?.id, getAccessTokenSilently, setAlert, t]);

    /** Reset to Details whenever a different team member is opened. */
    useEffect(() => {
        setActiveTab('details');
    }, [draft?.id]);

    // Load the caller's TEAM grant once per business (gates the whole screen).
    useEffect(() => {
        if (!businessIdStr) { setTeamGrant(null); setTeamGrantLoaded(false); return; }
        let cancelled = false;
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const g = await getMyPermissionGrant(businessIdStr, 'TEAM', token);
                if (!cancelled) { setTeamGrant(g); setTeamGrantLoaded(true); }
            } catch { if (!cancelled) { setTeamGrant(null); setTeamGrantLoaded(true); } }
        })();
        return () => { cancelled = true; };
    }, [businessIdStr, getAccessTokenSilently]);

    // Load the caller's COMMISSIONS grant once per business (gates the tab).
    useEffect(() => {
        if (!businessIdStr) { setCommissionGrant(null); return; }
        let cancelled = false;
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const g = await getMyPermissionGrant(businessIdStr, 'COMMISSIONS', token);
                if (!cancelled) setCommissionGrant(g);
            } catch { if (!cancelled) setCommissionGrant(null); }
        })();
        return () => { cancelled = true; };
    }, [businessIdStr, getAccessTokenSilently]);

    // Load commission config + history when a member is opened and viewable.
    useEffect(() => {
        const id = draft?.id;
        if (!id || !canViewCommission || !businessIdStr) {
            setCommDefault(''); setCommOverrides({}); setCommHistory([]); return;
        }
        let cancelled = false;
        (async () => {
            try {
                setCommLoading(true);
                const token = await getAccessTokenSilently();
                const [config, history] = await Promise.all([
                    getTeamMemberCommission(businessIdStr, String(id), token).catch(() => null),
                    getCommissionsByTeamMember(businessIdStr, String(id), token).catch(() => [] as CommissionRecord[]),
                ]);
                if (cancelled) return;
                setCommDefault(config?.defaultRate != null ? String(config.defaultRate) : '');
                const ov: Record<string, string> = {};
                (config?.overrides ?? []).forEach((o) => { ov[String(o.serviceId)] = String(o.rate); });
                setCommOverrides(ov);
                setCommHistory(history);
            } catch (e) {
                if (!cancelled) setAlert('danger', e instanceof Error ? e.message : t('teamMember.alerts.failCommission'));
            } finally {
                if (!cancelled) setCommLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [draft?.id, canViewCommission, businessIdStr, getAccessTokenSilently, setAlert, t]);

    const handleSaveCommission = async () => {
        if (!draft?.id || !businessIdStr) return;
        if (!canEditCommission) {
            setAlert('danger', t('teamMember.alerts.noPermissionCommission'));
            return;
        }
        // A commission is a percentage — reject anything outside 0–100 before saving
        // (the number input's min/max don't block typed-in values). Backend mirrors this.
        const inRange = (v: string) => {
            if (v == null || String(v).trim() === '') return true; // empty = clear
            const n = Number(v);
            return !isNaN(n) && n >= 0 && n <= 100;
        };
        if (!inRange(commDefault) || Object.values(commOverrides).some((v) => !inRange(v))) {
            setAlert('danger', t('teamMember.alerts.commissionRange'));
            return;
        }
        try {
            setCommSaving(true);
            const token = await getAccessTokenSilently();
            const overrides = Object.entries(commOverrides)
                .filter(([, v]) => v != null && String(v).trim() !== '' && !isNaN(Number(v)))
                .map(([serviceId, v]) => ({ serviceId, rate: Number(v) }));
            await saveTeamMemberCommission(
                {
                    businessId: businessIdStr,
                    teamMemberId: String(draft.id),
                    defaultRate: commDefault.trim() === '' ? null : Number(commDefault),
                    overrides,
                },
                token
            );
            setAlert('success', t('teamMember.alerts.commissionSaved'));
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('teamMember.alerts.failSaveCommission'));
        } finally {
            setCommSaving(false);
        }
    };

    /** Execute deactivation after confirm: check future appointments, then mutate. */
    const handleDeactivateConfirm = async () => {
        if (!deactivateTarget) return;
        const tm = deactivateTarget;
        setDeactivateBusy(true);
        try {
            const token = await getAccessTokenSilently();
            const appts = await getAppointmentsByTeamMember(String(tm.id), token);
            const futureActive = appts.filter(
                (a) =>
                    new Date(a.startTime).getTime() > Date.now() &&
                    !['CANCELLED', 'DECLINED', 'NO_SHOW'].includes(a.status)
            );
            if (futureActive.length > 0) {
                setDeactivateTarget(null);
                setAlert('danger', t('teamMember.alerts.hasFutureAppointments', { count: futureActive.length }));
                return;
            }
            // Optimistic update
            patchTeamMembers((prev) =>
                prev.map((x) => (x.id === tm.id ? { ...x, active: false } : x))
            );
            setDeactivateTarget(null);
            try {
                await setTeamMemberActive(tm.id, false, token);
                setAlert('success', t('teamMember.alerts.deactivated'));
            } catch (err) {
                // Revert on error
                patchTeamMembers((prev) =>
                    prev.map((x) => (x.id === tm.id ? { ...x, active: true } : x))
                );
                setAlert('danger', t('teamMember.alerts.statusError'));
            }
        } catch (err) {
            setAlert('danger', t('teamMember.alerts.statusError'));
        } finally {
            setDeactivateBusy(false);
        }
    };

    const startCreate = () => {
        setSelectedId(null);
        setSvcSearch(''); // don't carry the previous member's service search text
        setDraft(emptyDraft(colorForIndex(teamMembers.length), businessHours));
    };

    const startEdit = (tm: TeamMember) => {
        const idx = teamMembers.findIndex((t) => t.id === tm.id);
        setSelectedId(tm.id);
        setSvcSearch('');
        setDraft({
            id: tm.id,
            firstName: tm.firstName ?? '',
            lastName: tm.lastName ?? '',
            publicName: tm.publicName ?? '',
            email: tm.email ?? '',
            phone: tm.phone ?? '',
            color: resolveTeamMemberColor(tm, idx),
            allowOnlineBooking: tm.allowOnlineBooking ?? true,
            hasAppAccess: tm.hasAppAccess ?? false,
            roleBusinessId: tm.roleBusinessId ?? '',
            serviceIds: validServiceIds(tm.serviceIds),
            workingHours: defaultRows(tm, businessHours),
        });
    };

    const cancelEdit = () => {
        setSelectedId(null);
        setDraft(null);
    };

    const handleField = <K extends keyof Draft>(field: K, value: Draft[K]) => {
        setDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
    };

    const toggleService = (serviceId: string) => {
        if (!draft) return;
        const cur = draft.serviceIds ?? [];
        handleField(
            'serviceIds',
            cur.includes(serviceId) ? cur.filter((id) => id !== serviceId) : [...cur, serviceId]
        );
    };

    const updateRow = (index: number, patch: Partial<WorkingHourRow>) => {
        setDraft((prev) => {
            if (!prev) return prev;
            const rows = [...prev.workingHours];
            rows[index] = { ...rows[index], ...patch };
            return { ...prev, workingHours: rows };
        });
    };

    const handleSave = async () => {
        if (!draft) return;
        if (!draft.firstName.trim() || !draft.lastName.trim()) {
            setAlert('danger', t('teamMember.alerts.nameRequired'));
            return;
        }
        if (!businessIdStr) {
            setAlert('danger', t('teamMember.alerts.noBusinessSelected'));
            return;
        }
        // Online booking needs at least one service the member can perform (issue #35).
        if (draft.allowOnlineBooking && (draft.serviceIds?.length ?? 0) === 0) {
            setAlert('danger', t('teamMember.alerts.onlineBookingNeedsService'));
            return;
        }
        const phoneCountry = resolvePhoneCountry(undefined, selectedBusiness?.currency);
        if (!isPhoneComplete(draft.phone, phoneCountry)) {
            setAlert('danger', t('common.invalidPhone'));
            return;
        }

        // Validate enabled days
        for (const row of draft.workingHours) {
            if (!row.enabled) continue;
            if (!row.startTime || !row.endTime) {
                setAlert('danger', t('teamMember.alerts.workingHoursMissing', { day: dayLabel(row.dayOfWeek) }));
                return;
            }
            // Overnight requires the shift to close exactly at midnight (no gap before 00:00).
            if (row.overnightEnd && row.endTime !== '00:00') {
                setAlert('danger', t('teamMember.alerts.overnightNeedsMidnight', { day: dayLabel(row.dayOfWeek) }));
                return;
            }
            // The member's schedule must stay within the BUSINESS hours for that day
            // (issue #30). Fail-open when business hours aren't loaded (empty list).
            if (businessHours.length > 0) {
                const biz = businessHours.find(
                    (b) => String(b.dayOfWeek).toUpperCase() === String(row.dayOfWeek).toUpperCase()
                );
                if (!biz || !biz.openTime || !biz.closeTime) {
                    setAlert('danger', t('teamMember.alerts.dayBusinessClosed', { day: dayLabel(row.dayOfWeek) }));
                    return;
                }
                const bizOpen = hhmmToMin(biz.openTime);
                const bizClose = endMin(biz.closeTime);
                const memOpen = hhmmToMin(row.startTime);
                const memClose = endMin(row.endTime);
                if (bizOpen !== null && bizClose !== null && memOpen !== null && memClose !== null
                    && (memOpen < bizOpen || memClose > bizClose)) {
                    setAlert('danger', t('teamMember.alerts.outsideBusinessHours', {
                        day: dayLabel(row.dayOfWeek),
                        open: formatHHmm(biz.openTime.slice(0, 5), timeFmt),
                        close: formatHHmm(biz.closeTime.slice(0, 5), timeFmt),
                    }));
                    return;
                }
            }
        }

        const workingHoursInput: TeamMemberWorkingHoursInput[] = draft.workingHours
            .filter((r) => r.enabled)
            .map((r) => ({
                id: r.id ?? null,
                dayOfWeek: r.dayOfWeek,
                startTime: r.startTime,
                endTime: r.endTime,
                allowBookingOnIntervalTime: r.allowBookingOnIntervalTime,
                startIntervalTime: r.allowBookingOnIntervalTime ? null : r.startIntervalTime || null,
                endIntervalTime: r.allowBookingOnIntervalTime ? null : r.endIntervalTime || null,
                overnightEnd: r.overnightEnd || null,
            }));

        // Seats follow reality: above the professional included in the plan, a new
        // one costs more. Ask BEFORE saving instead of letting the backend reject —
        // on the free plan there is no seat to buy, so steer to the upgrade screen.
        let acceptExtraSeat = false;
        const isNew = !draft.id;
        if (isNew && plan && plan.teamCap > 0 && plan.teamMemberCount >= plan.teamCap) {
            if (plan.plan === 'AUTONOMO') {
                openUpsell('TEAM');
                return;
            }
            const ok = await confirmDialog({
                title: t('teamMember.seat.confirmTitle'),
                message: t('teamMember.seat.confirmMessage', { price: plan.extraProfessionalBrl }),
                confirmLabel: t('teamMember.seat.confirmCta'),
            });
            if (!ok) return;
            acceptExtraSeat = true;
        }

        const input: TeamMemberInput = {
            id: draft.id ?? null,
            firstName: draft.firstName.trim(),
            lastName: draft.lastName.trim(),
            publicName: draft.publicName.trim() || null,
            email: draft.email || null,
            phone: draft.phone || null,
            color: draft.color || null,
            businessId: businessIdStr,
            allowOnlineBooking: draft.allowOnlineBooking,
            hasAppAccess: draft.hasAppAccess,
            roleBusinessId: draft.roleBusinessId || null,
            workingHours: workingHoursInput,
            serviceIds: draft.serviceIds ?? [],
            acceptExtraSeat,
        };

        try {
            setSaving(true);
            const token = await getAccessTokenSilently();
            const saved = await saveTeamMember(input, token);
            setAlert('success', t('teamMember.alerts.saved', { name: `${saved.firstName} ${saved.lastName}` }));
            // The subscription grew by a seat — refresh so the Plano tab and the
            // crowns reflect the new cap immediately.
            if (acceptExtraSeat) reloadPlan();
            // Close the drawer on success (don't re-open the editor).
            cancelEdit();
            await reload();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('teamMember.alerts.failSave'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!draft?.id) return;
        // A hard delete orphans this member's appointments (they live in another
        // service with no FK). Block it up front when we already know they have
        // history, and steer to Deactivate — the backend enforces this too.
        if (!loadingTmData && tmAppointments.length > 0) {
            setAlert('danger', t('teamMember.alerts.hasAppointmentsCantDelete'));
            return;
        }
        if (!(await confirmDialog({ title: t('common.confirmTitle'), message: `Excluir o membro "${draft.firstName} ${draft.lastName ?? ''}"?`, danger: true, confirmLabel: t('common.delete') }))) return;
        try {
            setSaving(true);
            const token = await getAccessTokenSilently();
            await deleteTeamMember(draft.id, token);
            setAlert('success', t('teamMember.alerts.deleted'));
            cancelEdit();
            await reload();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('teamMember.alerts.failDelete'));
        } finally {
            setSaving(false);
        }
    };

    const roleNameById = (id?: string | null) =>
        roles.find((r) => String(r.id) === String(id))?.name ?? '—';

    if (loading || !teamGrantLoaded) return <LoadingComponent />;

    if (!canViewTeam) {
        return (
            <div className="list-card" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
                <h5 style={{ marginBottom: '0.5rem' }}>{t('noAccess.title')}</h5>
                <p className="meta">{t('teamMember.noAccess')}</p>
            </div>
        );
    }

    const q = search.trim().toLowerCase();
    const activeCount = teamMembers.filter((tm) => tm.active !== false).length;
    const inactiveCount = teamMembers.length - activeCount;
    // Scope the list to the selected tab first, then apply the text search.
    const tabMembers = teamMembers.filter((tm) =>
        statusTab === 'inactive' ? tm.active === false : tm.active !== false
    );
    const filteredMembers = q
        ? tabMembers.filter((tm) => {
              const name = `${tm.firstName ?? ''} ${tm.lastName ?? ''}`.toLowerCase();
              return (
                  name.includes(q) ||
                  (tm.email ?? '').toLowerCase().includes(q) ||
                  (tm.phone ?? '').toLowerCase().includes(q)
              );
          })
        : tabMembers;
    const totalPages = Math.max(1, Math.ceil(filteredMembers.length / perPage));
    const safePage = Math.min(page, totalPages);
    const pageStart = (safePage - 1) * perPage;
    const visibleMembers = filteredMembers.slice(pageStart, pageStart + perPage);

    return (
        <div className="team-member-page" data-cy="team-page">

            <div className="list-card" data-cy="team-list">
                <div className="list-header">
                    <h5 className="mb-0">{t('teamMember.title')}</h5>
                    <div className="gs-list-search">
                        <span className="gs-list-search__icon" aria-hidden>⌕</span>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder={t('teamMember.searchPlaceholder')}
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(1);
                            }}
                            data-cy="team-search-input"
                        />
                        {search && (
                            <button
                                type="button"
                                className="gs-list-search__clear"
                                onClick={() => {
                                    setSearch('');
                                    setPage(1);
                                }}
                                aria-label={t('common.clear')}
                                title={t('common.clear')}
                                data-cy="team-search-clear"
                            >
                                ×
                            </button>
                        )}
                    </div>
                    {canEditTeam && (
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={startCreate}
                            data-cy="team-add-btn"
                        >
                            {t('teamMember.addNew')}
                        </button>
                    )}
                </div>

                <div className="gs-tab-strip tm-status-tabs">
                    <button
                        type="button"
                        className={`gs-tab ${statusTab === 'active' ? 'gs-tab--active' : ''}`}
                        onClick={() => { setStatusTab('active'); setPage(1); }}
                        data-cy="team-tab-active"
                    >
                        {t('teamMember.tabs.active')}
                        <span className="gs-tab__count">{activeCount}</span>
                    </button>
                    <button
                        type="button"
                        className={`gs-tab ${statusTab === 'inactive' ? 'gs-tab--active' : ''}`}
                        onClick={() => { setStatusTab('inactive'); setPage(1); }}
                        data-cy="team-tab-inactive"
                    >
                        {t('teamMember.tabs.inactive')}
                        <span className="gs-tab__count">{inactiveCount}</span>
                    </button>
                </div>

                <div className="tm-table-wrap">
                    <table className="tm-table" data-cy="team-table">
                        <thead>
                            <tr>
                                <th>{t('teamMember.col.member')}</th>
                                <th>{t('teamMember.col.role')}</th>
                                <th className="tm-table__center">{t('teamMember.col.onlineBooking')}</th>
                                <th className="tm-table__center">{t('teamMember.col.appAccess')}</th>
                                <th className="tm-table__center">{t('teamMember.col.status')}</th>
                                <th>{t('teamMember.col.link')}</th>
                                {canEditTeam && <th>{t('teamMember.col.actions')}</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {visibleMembers.length === 0 && (
                                <tr>
                                    <td colSpan={canEditTeam ? 7 : 6} className="empty-hint">
                                        {teamMembers.length === 0
                                            ? t('teamMember.emptyList')
                                            : t('teamMember.emptySearch', { search })}
                                    </td>
                                </tr>
                            )}
                            {visibleMembers.map((tm) => {
                                const idx = teamMembers.findIndex((x) => x.id === tm.id);
                                const initials =
                                    (tm.firstName?.[0] ?? '?').toUpperCase() +
                                    (tm.lastName?.[0] ?? '').toUpperCase();
                                const color = resolveTeamMemberColor(tm, idx);
                                const roleName = roles.find((r) => String(r.id) === String(tm.roleBusinessId))?.name;
                                const isActive = tm.active !== false;
                                const hasContact = !!(tm.email || tm.phone);

                                return (
                                    <tr
                                        key={tm.id}
                                        className={`tm-table__row${isActive ? '' : ' tm-table__row--inactive'}${selectedId === tm.id ? ' tm-table__row--selected' : ''}`}
                                        onClick={() => startEdit(tm)}
                                        data-cy={`team-row-${tm.id}`}
                                    >
                                        {/* Merged Membro column: avatar + stacked name / email / phone */}
                                        <td className="tm-table__member-col">
                                            <div className="tm-table__member-cell">
                                                <div
                                                    className="avatar"
                                                    style={{
                                                        background: color,
                                                        color: readableTextColor(color),
                                                        opacity: isActive ? 1 : 0.45,
                                                    }}
                                                >
                                                    {initials}
                                                </div>
                                                <div className="tm-table__member-info">
                                                    <span className={`tm-table__name${isActive ? '' : ' tm-table__name--muted'}`}>
                                                        {tm.firstName} {tm.lastName}
                                                    </span>
                                                    {tm.email && (
                                                        <span className="tm-table__member-sub">{tm.email}</span>
                                                    )}
                                                    {tm.phone && (
                                                        <span className="tm-table__member-sub">{tm.phone}</span>
                                                    )}
                                                    {!hasContact && (
                                                        <span className="tm-table__member-sub">—</span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>

                                        {/* Role / Perfil */}
                                        <td data-cy={`team-role-badge-${tm.id}`}>
                                            <span className="tm-table__role">
                                                {roleName ?? '—'}
                                                {tm.isAdmin && (
                                                    <span className="tm-table__admin-badge">Admin</span>
                                                )}
                                            </span>
                                            {(() => {
                                                const count = validServiceIds(tm.serviceIds).length;
                                                return count > 0 ? (
                                                    <span className="tm-table__svc-count">
                                                        {t('teamMember.svcCount', { count })}
                                                    </span>
                                                ) : null;
                                            })()}
                                        </td>

                                        {/* Online booking */}
                                        <td className="tm-table__center">
                                            <span className={`tm-table__pill tm-table__pill--${tm.allowOnlineBooking ? 'yes' : 'no'}`}>
                                                {tm.allowOnlineBooking ? t('common.yes') : t('common.no')}
                                            </span>
                                        </td>

                                        {/* App access */}
                                        <td className="tm-table__center">
                                            <span className={`tm-table__pill tm-table__pill--${tm.hasAppAccess ? 'yes' : 'no'}`}>
                                                {tm.hasAppAccess ? t('common.yes') : t('common.no')}
                                            </span>
                                        </td>

                                        {/* Status */}
                                        <td className="tm-table__center">
                                            <span className={`tm-table__status-badge tm-table__status-badge--${isActive ? 'active' : 'inactive'}`}>
                                                {isActive ? t('teamMember.status.active') : t('teamMember.status.inactive')}
                                            </span>
                                        </td>

                                        {/* Share link — only for members who are actually bookable
                                            online (online booking ON + at least one service). */}
                                        <td>
                                            {tm.allowOnlineBooking && validServiceIds(tm.serviceIds).length > 0 ? (
                                                <button
                                                    type="button"
                                                    className="btn btn-link btn-sm p-0 tm-table__link-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setShareMemberName(`${tm.firstName ?? ''} ${tm.lastName ?? ''}`.trim());
                                                        setShareMemberServiceIds(validServiceIds(tm.serviceIds));
                                                        setShareModalOpen(true);
                                                    }}
                                                    title={t('teamMember.shareLink')}
                                                    data-cy={`team-share-row-${tm.id}`}
                                                >
                                                    {t('teamMember.shareLink')}
                                                </button>
                                            ) : (
                                                <span
                                                    className="text-muted"
                                                    title={t('teamMember.shareLinkUnavailable')}
                                                >
                                                    —
                                                </span>
                                            )}
                                        </td>

                                        {/* Activate / Deactivate — only when canEditTeam */}
                                        {canEditTeam && (
                                            <td>
                                                <button
                                                    type="button"
                                                    className={`btn btn-sm ${isActive ? 'btn-outline-secondary' : 'btn-outline-primary'} tm-table__toggle-btn`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (isActive) {
                                                            // Deactivate: open confirm modal first
                                                            setDeactivateTarget(tm);
                                                        } else {
                                                            // Activate: instant, no confirm needed
                                                            (async () => {
                                                                patchTeamMembers((prev) =>
                                                                    prev.map((x) =>
                                                                        x.id === tm.id ? { ...x, active: true } : x
                                                                    )
                                                                );
                                                                try {
                                                                    const token = await getAccessTokenSilently();
                                                                    await setTeamMemberActive(tm.id, true, token);
                                                                    setAlert('success', t('teamMember.alerts.activated'));
                                                                } catch (err) {
                                                                    patchTeamMembers((prev) =>
                                                                        prev.map((x) =>
                                                                            x.id === tm.id ? { ...x, active: false } : x
                                                                        )
                                                                    );
                                                                    setAlert('danger', t('teamMember.alerts.statusError'));
                                                                }
                                                            })();
                                                        }
                                                    }}
                                                    data-cy={`team-toggle-${tm.id}`}
                                                >
                                                    {isActive ? t('teamMember.deactivate') : t('teamMember.activate')}
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    page={safePage}
                    perPage={perPage}
                    totalItems={filteredMembers.length}
                    onPageChange={setPage}
                    onPerPageChange={(n) => {
                        setPerPage(n);
                        setPage(1);
                    }}
                    itemLabel="team member"
                />
            </div>

            <ConfirmModal
                open={!!deactivateTarget}
                title={t('teamMember.deactivateConfirm.title')}
                message={t('teamMember.deactivateConfirm.body', {
                    name: deactivateTarget
                        ? `${deactivateTarget.firstName ?? ''} ${deactivateTarget.lastName ?? ''}`.trim()
                        : '',
                })}
                confirmLabel={t('teamMember.deactivate')}
                danger
                busy={deactivateBusy}
                onConfirm={handleDeactivateConfirm}
                onCancel={() => setDeactivateTarget(null)}
                dataCy="team-deactivate-confirm-modal"
            />

            <ShareBookingModal
                open={shareModalOpen}
                onClose={() => setShareModalOpen(false)}
                slug={selectedBusiness?.slug}
                services={services.map((s) => ({ id: s.id, name: s.name ?? '' }))}
                initialMemberName={shareMemberName}
                initialMemberServiceIds={shareMemberServiceIds}
            />

            <Drawer
                open={!!draft}
                onClose={cancelEdit}
                title={draft?.id ? t('teamMember.drawerEdit') : t('teamMember.drawerNew')}
                size="xl"
                footer={
                    draft && (
                        <>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleSave}
                                disabled={saving}
                                data-cy="team-save-btn"
                            >
                                {saving ? t('common.working') : t('common.save')}
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={cancelEdit}
                                disabled={saving}
                                data-cy="team-cancel-btn"
                            >
                                {t('common.cancel')}
                            </button>
                            {draft.id && (
                                <button
                                    type="button"
                                    className="btn btn-outline-danger ms-auto"
                                    onClick={handleDelete}
                                    disabled={saving}
                                    data-cy="team-delete-btn"
                                >
                                    {t('common.delete')}
                                </button>
                            )}
                        </>
                    )
                }
            >
                {draft && (
                    <div data-cy="team-drawer">
                        {draft.id && (
                            <div className="gs-tab-strip">
                                <button
                                    type="button"
                                    className={`gs-tab ${activeTab === 'details' ? 'gs-tab--active' : ''}`}
                                    onClick={() => setActiveTab('details')}
                                    data-cy="team-tab-details"
                                >
                                    {t('teamMember.tabDetails')}
                                </button>
                                <button
                                    type="button"
                                    className={`gs-tab ${activeTab === 'appointments' ? 'gs-tab--active' : ''}`}
                                    onClick={() => setActiveTab('appointments')}
                                    data-cy="team-tab-appointments"
                                >
                                    {t('teamMember.tabAppointments')}
                                    {tmAppointments.length > 0 && (
                                        <span className="gs-tab__count">{tmAppointments.length}</span>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    className={`gs-tab ${activeTab === 'payments' ? 'gs-tab--active' : ''}`}
                                    onClick={() => setActiveTab('payments')}
                                    data-cy="team-tab-payments"
                                >
                                    {t('teamMember.tabPayments')}
                                    {tmTxns.length > 0 && (
                                        <span className="gs-tab__count">{tmTxns.length}</span>
                                    )}
                                </button>
                                {canViewCommission && (
                                    <button
                                        type="button"
                                        className={`gs-tab ${activeTab === 'commission' ? 'gs-tab--active' : ''}`}
                                        onClick={() => setActiveTab('commission')}
                                        data-cy="team-tab-commission"
                                    >
                                        {t('teamMember.tabCommission')}
                                        {commHistory.length > 0 && (
                                            <span className="gs-tab__count">{commHistory.length}</span>
                                        )}
                                    </button>
                                )}
                            </div>
                        )}

                        {activeTab === 'details' && (
                          <>
                            <div className="row">
                                <div className="col-md-6 form-row">
                                    <label className="form-label">{t('teamMember.firstName')}</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={draft.firstName}
                                        maxLength={100}
                                        onChange={(e) => handleField('firstName', e.target.value)}
                                        data-cy="team-firstName-input"
                                    />
                                </div>
                                <div className="col-md-6 form-row">
                                    <label className="form-label">{t('teamMember.lastName')}</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={draft.lastName}
                                        maxLength={100}
                                        onChange={(e) => handleField('lastName', e.target.value)}
                                        data-cy="team-lastName-input"
                                    />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-12 form-row">
                                    <label className="form-label">{t('teamMember.publicName')}</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={draft.publicName}
                                        maxLength={120}
                                        onChange={(e) => handleField('publicName', e.target.value)}
                                        placeholder={t('teamMember.publicNamePlaceholder')}
                                        data-cy="team-publicName-input"
                                    />
                                    <small className="form-hint">{t('teamMember.publicNameHint')}</small>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-7 form-row">
                                    <label className="form-label">{t('teamMember.email')}</label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        value={draft.email}
                                        maxLength={255}
                                        onChange={(e) => handleField('email', e.target.value)}
                                        data-cy="team-email-input"
                                    />
                                </div>
                                <div className="col-md-5 form-row">
                                    <label className="form-label">{t('teamMember.phone')}</label>
                                    <input
                                        type="tel"
                                        className="form-control"
                                        value={draft.phone}
                                        maxLength={16}
                                        onChange={(e) => {
                                            const country = resolvePhoneCountry(undefined, selectedBusiness?.currency);
                                            handleField('phone', formatPhone(e.target.value, country));
                                        }}
                                        data-cy="team-phone-input"
                                    />
                                    {!isPhoneComplete(draft.phone, resolvePhoneCountry(undefined, selectedBusiness?.currency)) &&
                                     draft.phone !== '' && (
                                        <span className="form-text text-danger small">{t('common.invalidPhone')}</span>
                                    )}
                                </div>
                            </div>

                            <div className="form-row">
                                <label className="form-label">{t('teamMember.color')}</label>
                                <div className="d-flex flex-wrap gap-2 align-items-center">
                                    {PASTEL_PALETTE.map((c) => {
                                        const selected =
                                            draft.color.toUpperCase() === c.toUpperCase();
                                        return (
                                            <button
                                                key={c}
                                                type="button"
                                                onClick={() => handleField('color', c)}
                                                title={c}
                                                aria-label={`Pick color ${c}`}
                                                style={{
                                                    width: 28,
                                                    height: 28,
                                                    borderRadius: '50%',
                                                    border: selected
                                                        ? '3px solid #333'
                                                        : '2px solid #ddd',
                                                    background: c,
                                                    cursor: 'pointer',
                                                    padding: 0,
                                                }}
                                            />
                                        );
                                    })}
                                    <label
                                        title={t('services.pickColor')}
                                        style={{
                                            position: 'relative',
                                            width: 28,
                                            height: 28,
                                            borderRadius: '50%',
                                            border: '2px dashed #888',
                                            cursor: 'pointer',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: PASTEL_PALETTE.some(
                                                (p) => p.toUpperCase() === draft.color.toUpperCase()
                                            )
                                                ? 'transparent'
                                                : draft.color,
                                            color: '#555',
                                            fontSize: 16,
                                            fontWeight: 'bold',
                                            margin: 0,
                                        }}
                                    >
                                        +
                                        <input
                                            type="color"
                                            value={draft.color}
                                            onChange={(e) => handleField('color', e.target.value)}
                                            style={{
                                                position: 'absolute',
                                                inset: 0,
                                                opacity: 0,
                                                cursor: 'pointer',
                                                width: '100%',
                                                height: '100%',
                                            }}
                                        />
                                    </label>
                                    <span
                                        className="meta"
                                        style={{
                                            marginLeft: '0.5rem',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '0.35rem',
                                        }}
                                    >
                                        <span
                                            style={{
                                                display: 'inline-block',
                                                width: 14,
                                                height: 14,
                                                borderRadius: 3,
                                                background: draft.color,
                                                border: '1px solid #ccc',
                                            }}
                                        />
                                        {draft.color.toUpperCase()}
                                    </span>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-7 form-row">
                                    <label className="form-label">{t('teamMember.role')}</label>
                                    <select
                                        className="form-select"
                                        value={draft.roleBusinessId}
                                        onChange={(e) => handleField('roleBusinessId', e.target.value)}
                                        data-cy="team-role-select"
                                    >
                                        <option value="">{t('teamMember.noRole')}</option>
                                        {roles.map((r) => (
                                            <option key={r.id} value={r.id}>
                                                {r.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-5 form-row d-flex align-items-end">
                                    <div
                                        className="d-flex flex-column"
                                        style={{ gap: '0.5rem' }}
                                    >
                                        <div className="form-check">
                                            <input
                                                id="tm-online-booking"
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={draft.allowOnlineBooking}
                                                onChange={(e) =>
                                                    handleField('allowOnlineBooking', e.target.checked)
                                                }
                                                data-cy="team-allow-online-booking-checkbox"
                                            />
                                            <label
                                                className="form-check-label"
                                                htmlFor="tm-online-booking"
                                            >
                                                {t('teamMember.allowOnlineBooking')}
                                            </label>
                                        </div>
                                        {/* Grants this person the right to sign into the admin
                                            app and see this business. Off by default — not
                                            everyone needs login access. */}
                                        <div className="form-check">
                                            <input
                                                id="tm-app-access"
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={draft.hasAppAccess}
                                                onChange={(e) =>
                                                    handleField('hasAppAccess', e.target.checked)
                                                }
                                                data-cy="team-hasAppAccess-checkbox"
                                            />
                                            <label
                                                className="form-check-label"
                                                htmlFor="tm-app-access"
                                            >
                                                {t('teamMember.hasAppAccess')}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="form-row tm-services-field" data-cy="team-services-field">
                                <div className="tm-services-field__head">
                                    <label className="form-label">{t('teamMember.services')}</label>
                                    {services.length > 0 && (
                                        <span className="meta">
                                            {t('teamMember.servicesCount', {
                                                selected: draft.serviceIds.filter((id) => services.some((s) => s.id === id)).length,
                                                total: services.length,
                                            })}
                                        </span>
                                    )}
                                </div>
                                {services.length === 0 ? (
                                    <div className="empty-hint" style={{ padding: '0.25rem 0' }}>
                                        {t('teamMember.noServicesYet')}
                                    </div>
                                ) : (
                                    (() => {
                                        const selectedServices = draft.serviceIds
                                            .map((id) => services.find((s) => s.id === id))
                                            .filter((s): s is Service => !!s);
                                        const q = svcSearch.trim().toLowerCase();
                                        const availableMatches = services
                                            .filter((s) => !draft.serviceIds.includes(s.id))
                                            .filter(
                                                (s) =>
                                                    !q ||
                                                    (s.name ?? '').toLowerCase().includes(q)
                                            )
                                            .slice(0, 8);
                                        return (
                                            <>
                                                <div className="tm-services-field__search">
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        placeholder={
                                                            selectedServices.length ===
                                                            services.length
                                                                ? t('teamMember.allServicesSelected')
                                                                : t('teamMember.addService')
                                                        }
                                                        value={svcSearch}
                                                        disabled={
                                                            selectedServices.length ===
                                                            services.length
                                                        }
                                                        onChange={(e) =>
                                                            setSvcSearch(e.target.value)
                                                        }
                                                        onFocus={() => setSvcDropdownOpen(true)}
                                                        onBlur={() =>
                                                            setTimeout(
                                                                () => setSvcDropdownOpen(false),
                                                                150
                                                            )
                                                        }
                                                        data-cy="team-services-search"
                                                    />
                                                    {svcDropdownOpen &&
                                                        availableMatches.length > 0 && (
                                                            <div
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: '100%',
                                                                    left: 0,
                                                                    right: 0,
                                                                    zIndex: 10,
                                                                    background: 'var(--gs-surface, #fff)',
                                                                    border: '1px solid var(--gs-border, #e6e6ec)',
                                                                    borderRadius: 6,
                                                                    boxShadow:
                                                                        '0 4px 14px rgba(0,0,0,0.08)',
                                                                    maxHeight: 240,
                                                                    overflowY: 'auto',
                                                                    marginTop: 2,
                                                                }}
                                                            >
                                                                {availableMatches.map((s) => (
                                                                    <button
                                                                        key={s.id}
                                                                        type="button"
                                                                        onMouseDown={(e) => {
                                                                            e.preventDefault();
                                                                            toggleService(s.id);
                                                                            setSvcSearch('');
                                                                        }}
                                                                        style={{
                                                                            display: 'block',
                                                                            width: '100%',
                                                                            textAlign: 'left',
                                                                            padding: '0.5rem 0.75rem',
                                                                            background:
                                                                                'transparent',
                                                                            border: 'none',
                                                                            borderBottom:
                                                                                '1px solid var(--gs-divider, #f0f0f3)',
                                                                            cursor: 'pointer',
                                                                        }}
                                                                    >
                                                                        {s.name || '(no name)'}
                                                                        {s.totalDuration ? (
                                                                            <span
                                                                                style={{
                                                                                    color: '#888',
                                                                                    fontSize: '0.85em',
                                                                                    marginLeft: 6,
                                                                                }}
                                                                            >
                                                                                · {s.totalDuration} min
                                                                            </span>
                                                                        ) : null}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}
                                                </div>

                                                {/* Selected services — chips BELOW the search. */}
                                                <div className="tm-services-field__chips" data-cy="team-services-chips">
                                                    {selectedServices.length === 0 && (
                                                        <span className="meta">
                                                            {t('teamMember.noneSelectedYet')}
                                                        </span>
                                                    )}
                                                    {selectedServices.map((s) => (
                                                        <span key={s.id} className="gs-chip">
                                                            {s.name || '(no name)'}
                                                            <button
                                                                type="button"
                                                                className="gs-chip__close"
                                                                onClick={() => toggleService(s.id)}
                                                                aria-label={`${t('common.remove')} ${s.name}`}
                                                                title={t('common.remove')}
                                                            >
                                                                ×
                                                            </button>
                                                        </span>
                                                    ))}
                                                </div>
                                            </>
                                        );
                                    })()
                                )}
                            </div>

                            <div className="form-row">
                                <label className="form-label">{t('teamMember.workingHours')}</label>
                                <div className="hours-table-wrap">
                                <table className="hours-table" data-cy="team-working-hours-table">
                                    <thead>
                                        <tr>
                                            <th></th>
                                            <th>{t('teamMember.whDay')}</th>
                                            <th>{t('teamMember.whStart')}</th>
                                            <th>{t('teamMember.whEnd')}</th>
                                            <th>{t('teamMember.whBreak')}</th>
                                            <th>{t('teamMember.whBreakStart')}</th>
                                            <th>{t('teamMember.whBreakEnd')}</th>
                                            <th>{t('teamMember.whOvernight')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {draft.workingHours.map((row, index) => (
                                            <tr
                                                key={row.dayOfWeek}
                                                className={!row.enabled ? 'row-disabled' : ''}
                                            >
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={row.enabled}
                                                        onChange={(e) =>
                                                            updateRow(index, {
                                                                enabled: e.target.checked,
                                                            })
                                                        }
                                                    />
                                                </td>
                                                <td>{dayLabel(row.dayOfWeek)}</td>
                                                <td>
                                                    <TimeSelect
                                                        value={row.startTime}
                                                        disabled={!row.enabled}
                                                        onChange={(v) => updateRow(index, { startTime: v })}
                                                    />
                                                </td>
                                                <td>
                                                    <TimeSelect
                                                        value={row.endTime}
                                                        disabled={!row.enabled}
                                                        onChange={(v) => updateRow(index, { endTime: v })}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={!row.allowBookingOnIntervalTime}
                                                        disabled={!row.enabled}
                                                        onChange={(e) =>
                                                            updateRow(index, {
                                                                allowBookingOnIntervalTime:
                                                                    !e.target.checked,
                                                            })
                                                        }
                                                    />
                                                </td>
                                                <td>
                                                    <TimeSelect
                                                        value={row.startIntervalTime}
                                                        disabled={!row.enabled || row.allowBookingOnIntervalTime}
                                                        onChange={(v) => updateRow(index, { startIntervalTime: v })}
                                                    />
                                                </td>
                                                <td>
                                                    <TimeSelect
                                                        value={row.endIntervalTime}
                                                        disabled={!row.enabled || row.allowBookingOnIntervalTime}
                                                        onChange={(v) => updateRow(index, { endIntervalTime: v })}
                                                    />
                                                </td>
                                                <td>
                                                    <div className="wh-overnight-cell" title={t('teamMember.whOvernightHint')}>
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            data-cy={`team-wh-overnight-${row.dayOfWeek.toLowerCase()}`}
                                                            checked={row.overnightEnd != null}
                                                            disabled={!row.enabled}
                                                            onChange={(e) =>
                                                                updateRow(index, e.target.checked
                                                                    // Overnight requires the shift to end at midnight
                                                                    // (no gap before 00:00) — snap FIM to 00:00.
                                                                    ? { overnightEnd: '02:00', endTime: '00:00' }
                                                                    : { overnightEnd: null })
                                                            }
                                                        />
                                                        {row.overnightEnd != null && (
                                                            <TimeSelect
                                                                value={row.overnightEnd}
                                                                disabled={!row.enabled}
                                                                onChange={(v) =>
                                                                    updateRow(index, { overnightEnd: v || null })
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                </div>
                                <div className="meta">
                                    {t('teamMember.whBreakHint')}
                                </div>
                            </div>
                          </>
                        )}

                        {activeTab === 'appointments' && (
                            <div className="gs-client-history">
                                {loadingTmData && (
                                    <div className="gs-hint">{t('teamMember.loadingHistory')}</div>
                                )}
                                {!loadingTmData && tmAppointments.length === 0 && (
                                    <div className="gs-hint">
                                        {t('teamMember.noAppointments')}
                                    </div>
                                )}
                                {tmAppointments.map((a) => {
                                    const start = new Date(a.startTime);
                                    const end = new Date(a.endTime);
                                    const isPast = end.getTime() < Date.now();
                                    const pmeta = APPOINTMENT_PAYMENT_LABEL[a.paymentStatus];
                                    return (
                                        <div
                                            key={a.id}
                                            className={`gs-client-history__item gs-client-history__item--link ${isPast ? 'gs-client-history__item--past' : ''}`}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => openAppointmentDetail(a.id)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    openAppointmentDetail(a.id);
                                                }
                                            }}
                                            title={t('teamMember.openAppointment')}
                                        >
                                            <div className="gs-client-history__when">
                                                <span className="gs-client-history__id">#{a.id}</span>
                                                <strong>
                                                    {start.toLocaleDateString(undefined, {
                                                        weekday: 'short',
                                                        month: 'short',
                                                        day: 'numeric',
                                                        year: start.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
                                                    })}
                                                </strong>
                                                <span className="gs-client-history__time">
                                                    {formatClock(start, timeFmt)}
                                                    {' – '}
                                                    {formatClock(end, timeFmt)}
                                                </span>
                                                <span className={`gs-client-history__status gs-client-history__status--${a.status.toLowerCase()}`}>
                                                    {a.status}
                                                </span>
                                                {pmeta && (
                                                    <span className={`payments-appt-badge payments-appt-badge--${pmeta.tone}`}>
                                                        {pmeta.label}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="gs-client-history__customer">
                                                {a.customerName}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {activeTab === 'payments' && (() => {
                            // Roll up totals to give the user a quick sense of
                            // how much this team member generated. Refunds are
                            // subtracted so the "net" matches what their book
                            // of business actually brought in.
                            const succeeded = tmTxns.filter(
                                (t) => t.status === 'SUCCEEDED' || t.status === 'PARTIALLY_REFUNDED'
                            );
                            const gross = succeeded.reduce((s, tx) => s + tx.amount, 0);
                            const refunded = succeeded.reduce((s, tx) => s + (tx.refundedAmount || 0), 0);
                            const net = gross - refunded;
                            const cur = succeeded[0]?.currency || 'USD';
                            const fmt = (n: number) => {
                                try {
                                    return new Intl.NumberFormat(undefined, {
                                        style: 'currency',
                                        currency: cur,
                                    }).format(n);
                                } catch {
                                    return `${cur} ${n.toFixed(2)}`;
                                }
                            };
                            return (
                                <div className="gs-client-history">
                                    {loadingTmData && (
                                        <div className="gs-hint">{t('teamMember.loadingPayments')}</div>
                                    )}
                                    {!loadingTmData && tmTxns.length === 0 && (
                                        <div className="gs-hint">
                                            {t('teamMember.noPayments')}
                                        </div>
                                    )}
                                    {tmTxns.length > 0 && (
                                        <div className="tm-payments-summary">
                                            <div><span>{t('teamMember.paymentNetGenerated')}</span><strong>{fmt(net)}</strong></div>
                                            <div><span>{t('teamMember.paymentGross')}</span><strong>{fmt(gross)}</strong></div>
                                            <div><span>{t('teamMember.paymentRefunded')}</span><strong>{fmt(refunded)}</strong></div>
                                            <div><span>{t('teamMember.paymentTransactions')}</span><strong>{succeeded.length}</strong></div>
                                        </div>
                                    )}
                                    {tmTxns
                                        .slice()
                                        .sort((a, b) => (a.createdOn < b.createdOn ? 1 : -1))
                                        .map((tx) => {
                                            const when = new Date(tx.createdOn);
                                            const txNet = tx.amount - (tx.refundedAmount || 0);
                                            return (
                                                <div
                                                    key={tx.id}
                                                    className={`gs-client-history__item ${tx.appointmentId ? 'gs-client-history__item--link' : ''}`}
                                                    role={tx.appointmentId ? 'button' : undefined}
                                                    tabIndex={tx.appointmentId ? 0 : undefined}
                                                    onClick={() => tx.appointmentId && openAppointmentDetail(tx.appointmentId)}
                                                    title={tx.appointmentId ? t('teamMember.openAppointment') : undefined}
                                                >
                                                    <div className="gs-client-history__when">
                                                        {tx.appointmentId && (
                                                            <span className="gs-client-history__id">#{tx.appointmentId}</span>
                                                        )}
                                                        <strong>{fmt(txNet)}</strong>
                                                        <span className="gs-client-history__time">
                                                            {t(`payments.method.${tx.method}`)} · {when.toLocaleDateString()}
                                                        </span>
                                                        <span className={`gs-client-history__status gs-client-history__status--${tx.status.toLowerCase()}`}>
                                                            {tx.status}
                                                        </span>
                                                    </div>
                                                    <div className="gs-client-history__customer">
                                                        {tx.customerName || '—'}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                </div>
                            );
                        })()}

                        {activeTab === 'commission' && (() => {
                            const total = commHistory.reduce((s, c) => s + (c.amount ?? 0), 0);
                            const cur = commHistory[0]?.currency || 'USD';
                            const fmt = (n: number) => {
                                try {
                                    return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(n);
                                } catch { return `${cur} ${n.toFixed(2)}`; }
                            };
                            const serviceName = (id?: string | null) =>
                                services.find((s) => String(s.id) === String(id))?.name ?? (id ?? '—');
                            const availableToAdd = services.filter((s) => !(String(s.id) in commOverrides));
                            return (
                                <>
                                    {/* ---- Config (managers only) ---- */}
                                    <div className="business-section-container">
                                        <h3 className="section-header">{t('teamMember.commissionConfig')}</h3>
                                        <div className="form-text" style={{ marginBottom: '0.75rem' }}>
                                            {t('teamMember.commissionConfigHint')}
                                        </div>
                                        <div className="form-row" style={{ maxWidth: 220 }}>
                                            <label className="form-label">{t('teamMember.commissionDefault')}</label>
                                            {/* Percent field — NumberWithSuffix blocks typing above 100
                                                (and rejects letters/negatives), same as the discount tiers. */}
                                            <NumberWithSuffix
                                                id="team-commission-default-input"
                                                value={commDefault}
                                                onChange={(v) => setCommDefault(v)}
                                                suffix="%"
                                                max={100}
                                                placeholder="0"
                                                disabled={!canEditCommission}
                                            />
                                        </div>

                                        <div style={{ marginTop: '1rem' }}>
                                            <label className="form-label">{t('teamMember.commissionOverrides')}</label>
                                            {Object.keys(commOverrides).length === 0 && (
                                                <div className="gs-hint">{t('teamMember.commissionNoOverrides')}</div>
                                            )}
                                            {Object.entries(commOverrides).map(([sid, val]) => (
                                                <div key={sid} className="d-flex align-items-center gap-2 mb-2">
                                                    <span style={{ flex: 1 }}>{serviceName(sid)}</span>
                                                    <div style={{ width: 120 }}>
                                                        <NumberWithSuffix
                                                            value={val}
                                                            onChange={(v) => setCommOverrides((p) => ({ ...p, [sid]: v }))}
                                                            suffix="%"
                                                            max={100}
                                                            placeholder="0"
                                                            disabled={!canEditCommission}
                                                        />
                                                    </div>
                                                    {canEditCommission && (
                                                        <button type="button" className="btn btn-link btn-sm text-danger p-0"
                                                            onClick={() => setCommOverrides((p) => {
                                                                const n = { ...p }; delete n[sid]; return n;
                                                            })}>{t('teamMember.commissionRemove')}</button>
                                                    )}
                                                </div>
                                            ))}
                                            {canEditCommission && availableToAdd.length > 0 && (
                                                <div className="d-flex align-items-center gap-2 mt-2">
                                                    <select className="form-select" style={{ maxWidth: 280 }}
                                                        value={addServiceId}
                                                        onChange={(e) => setAddServiceId(e.target.value)}
                                                        data-cy="team-commission-service-select">
                                                        <option value="">{t('teamMember.commissionAddOverride')}</option>
                                                        {availableToAdd.map((s) => (
                                                            <option key={s.id} value={String(s.id)}>{s.name}</option>
                                                        ))}
                                                    </select>
                                                    <button type="button" className="btn btn-outline-secondary btn-sm"
                                                        disabled={!addServiceId}
                                                        onClick={() => {
                                                            if (!addServiceId) return;
                                                            setCommOverrides((p) => ({ ...p, [addServiceId]: commDefault || '0' }));
                                                            setAddServiceId('');
                                                        }}>{t('teamMember.commissionAdd')}</button>
                                                </div>
                                            )}
                                        </div>

                                        {canEditCommission && (
                                            <div style={{ marginTop: '1rem' }}>
                                                <button type="button" className="btn btn-primary btn-sm"
                                                    onClick={handleSaveCommission} disabled={commSaving}
                                                    data-cy="team-commission-save-btn">
                                                    {commSaving ? t('teamMember.commissionSaving') : t('teamMember.commissionSave')}
                                                </button>
                                            </div>
                                        )}
                                        {!canEditCommission && (
                                            <div className="form-text mt-2">
                                                {t('teamMember.commissionViewOnly')}
                                            </div>
                                        )}
                                    </div>

                                    {/* ---- History ---- */}
                                    <div className="business-section-container" style={{ marginTop: '1rem' }}>
                                        <h3 className="section-header">{t('teamMember.commissionHistory')}</h3>
                                        {commLoading && <div className="gs-hint">{t('teamMember.commissionLoading')}</div>}
                                        {!commLoading && commHistory.length === 0 && (
                                            <div className="gs-hint">{t('teamMember.commissionNoHistory')}</div>
                                        )}
                                        {commHistory.length > 0 && (
                                            <div className="tm-payments-summary">
                                                <div><span>{t('teamMember.commissionTotal')}</span><strong>{fmt(total)}</strong></div>
                                                <div><span>{t('teamMember.commissionRecords')}</span><strong>{commHistory.length}</strong></div>
                                            </div>
                                        )}
                                        <div className="gs-client-history">
                                            {commHistory.map((c) => {
                                                const when = c.createdOn ? new Date(c.createdOn) : null;
                                                return (
                                                    <div key={c.id} className="gs-client-history__item">
                                                        <div className="gs-client-history__when">
                                                            <strong>{fmt(c.amount ?? 0)}</strong>
                                                            <span className="gs-client-history__time">
                                                                {t('teamMember.commissionRate', { rate: c.rate ?? 0, amount: fmt(c.baseAmount ?? 0) })}
                                                                {when && ` · ${when.toLocaleDateString()}`}
                                                            </span>
                                                        </div>
                                                        <div className="gs-client-history__customer">
                                                            {serviceName(c.serviceId)}
                                                            {c.appointmentId ? ` · #${c.appointmentId}` : ''}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                )}
            </Drawer>
        </div>
    );
};

export default TeamMemberComponent;
